// utils/landingPath.ts
/**
 * Resolves the per-account landing destination to a concrete, VISIBLE leaf route.
 *
 * The returned path is always one whose own MenuGuard passes (or the always-
 * reachable settings fallback), so routing any "go home" redirect through this
 * resolver can never bounce forever. See constants/sectionPaths.ts for why a
 * visible *leaf* (not a section-index path) is required.
 */

import {
  DEFAULT_LANDING_SECTION,
  LANDING_FALLBACK_PATH,
  MAIN_SECTION_KEYS,
  SECTION_SUBMENU_LEAVES,
} from "@/constants/sectionPaths";

type IsMenuVisible = (menuKey: string) => boolean;
type IsSubmenuVisible = (menuKey: string, submenuKey: string) => boolean;

/**
 * @param landingSection the account's configured landing section, or null/undefined
 *   for the default ("dashboard").
 * @param isMenuVisible from useMenuVisibility.
 * @param isSubmenuVisible from useMenuVisibility.
 * @returns a visible leaf route, or LANDING_FALLBACK_PATH if nothing is visible.
 */
export function resolveLandingPath(
  landingSection: string | null | undefined,
  isMenuVisible: IsMenuVisible,
  isSubmenuVisible: IsSubmenuVisible
): string {
  // Configured section first, then the rest in priority order (deduped).
  const preferred = landingSection || DEFAULT_LANDING_SECTION;
  const candidates = [
    preferred,
    ...MAIN_SECTION_KEYS.filter((k) => k !== preferred),
  ];

  for (const section of candidates) {
    if (!isMenuVisible(section)) continue;

    if (section === "dashboard") {
      // No submenus — the section itself is the leaf.
      return "/dashboard";
    }

    const leaves = SECTION_SUBMENU_LEAVES[section] ?? [];
    const firstVisible = leaves.find((leaf) =>
      isSubmenuVisible(section, leaf.submenu)
    );
    if (firstVisible) return firstVisible.path;
    // Section visible but no visible submenu leaf — fall through to next candidate.
  }

  // Pathological config (everything hidden): core settings are always reachable.
  return LANDING_FALLBACK_PATH;
}
